import { useEffect, useRef, useState } from 'react';
import { ChoiceMessage, ExperienceGain, GameState } from '../types';
import { CityPanel } from './CityPanel';
import { ExperienceProgress } from './ExperienceProgress';
import { RecapTimeline } from './RecapTimeline';

const STREAK_MILESTONES = [5, 10, 15, 20];

interface Props {
  state: GameState;
  lastMessage: ChoiceMessage | null;
  onChoose: (choice: 'left' | 'right') => void;
  onBack: () => void;
  onRestartRandom: () => void;
  onRestartWithSeed: (seed: string) => void;
  seedValue: string;
  onSeedChange: (value: string) => void;
  totalExp: number;
  recentGain: ExperienceGain | null;
  modeLabel: string;
  onRestartSameMode?: () => void;
}

export function GameView({
  state,
  lastMessage,
  onChoose,
  onBack,
  onRestartRandom,
  onRestartWithSeed,
  seedValue,
  onSeedChange,
  totalExp,
  recentGain,
  modeLabel,
  onRestartSameMode,
}: Props) {
  const [isRevealing, setIsRevealing] = useState(false);
  const [hasRevealed, setHasRevealed] = useState(false);
  const [displayedPopulation, setDisplayedPopulation] = useState(0);
  const [winnerSide, setWinnerSide] = useState<'left' | 'right' | null>(null);
  const [milestoneHit, setMilestoneHit] = useState<{ value: number; key: number } | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const delayRef = useRef<number | null>(null);
  const selectedChoiceRef = useRef<'left' | 'right' | null>(null);
  const previousStreakRef = useRef(state.streak);

  useEffect(() => {
    setIsRevealing(false);
    setHasRevealed(false);
    setDisplayedPopulation(0);
    setWinnerSide(null);
    selectedChoiceRef.current = null;
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (delayRef.current) {
      window.clearTimeout(delayRef.current);
      delayRef.current = null;
    }
  }, [state.challengerCity.id]);

  useEffect(() => () => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (delayRef.current) {
      window.clearTimeout(delayRef.current);
      delayRef.current = null;
    }
  }, []);

  useEffect(() => {
    const previous = previousStreakRef.current;
    if (state.streak < previous) {
      setMilestoneHit(null);
    }
    const reached = STREAK_MILESTONES.find((value) => state.streak === value && previous < value);
    if (reached) {
      setMilestoneHit({ value: reached, key: Date.now() });
    }
    previousStreakRef.current = state.streak;
  }, [state.streak]);

  // Runs a lightweight interval-driven count-up animation to reveal the challenger population.
  const animatePopulationReveal = (target: number, duration = 900) => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }
    const start = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(progress);
      const nextValue = Math.round(target * eased);
      setDisplayedPopulation(nextValue);

      if (progress < 1) {
        animationFrameRef.current = window.requestAnimationFrame(step);
        return;
      }

      animationFrameRef.current = null;
      setHasRevealed(true);
      const computedWinner = state.currentCity.population >= state.challengerCity.population ? 'left' : 'right';
      setWinnerSide(computedWinner);
      delayRef.current = window.setTimeout(() => {
        setIsRevealing(false);
        onChoose(selectedChoiceRef.current ?? 'right');
      }, 1000);
    };

    animationFrameRef.current = window.requestAnimationFrame(step);
  };

  const handleChoose = (choice: 'left' | 'right') => {
    if (state.isOver || isRevealing) return;
    selectedChoiceRef.current = choice;
    setIsRevealing(true);
    animatePopulationReveal(state.challengerCity.population);
  };

  return (
    <section>
      <div className="game-topline">
        <button className="btn ghost" onClick={onBack}>
          {state.isOver ? 'Menu' : 'Quit'}
        </button>
        <div
          key={milestoneHit?.key ?? 'streak-banner'}
          className={`streak-banner${milestoneHit ? ' streak-milestone' : ''}`}
          data-milestone={milestoneHit?.value ?? ''}
          aria-live="polite"
        >
          <span className="streak-label">Current streak</span>
          <span className="streak-value">{state.streak}</span>
        </div>
        <span className="pill neutral mode-pill" aria-live="polite">
          {modeLabel}
        </span>
      </div>
      <div className="game-stage" aria-live="polite">
        {state.isOver ? (
          <RecapTimeline rounds={state.roundHistory} />
        ) : (
          <>
            <div className="divider-line" aria-hidden />
            <CityPanel
              city={state.currentCity}
              position="left"
              showPopulation
              isWinner={winnerSide === 'left'}
              onChoose={handleChoose}
              disabled={isRevealing || state.isOver}
            />
            <CityPanel
              city={state.challengerCity}
              position="right"
              showPopulation={hasRevealed || isRevealing}
              isWinner={winnerSide === 'right'}
              populationValue={hasRevealed || isRevealing ? displayedPopulation : undefined}
              onChoose={handleChoose}
              disabled={isRevealing || state.isOver}
            />
          </>
        )}
      </div>
      {state.isOver ? (
        <>
          <div className="footer-row">
            <div>Game over. Your streak ended at {state.streak}.</div>
            {state.mode === 'random' || state.mode === 'daily' ? (
              <div className="nav-row" style={{ flexWrap: 'nowrap', alignItems: 'center', gap: '0.5rem' }}>
                <button className="btn primary" onClick={onRestartRandom}>
                  Random
                </button>
                <input
                  id="gameover-seed-input"
                  className="text-input"
                  value={seedValue}
                  onChange={(e) => onSeedChange(e.target.value)}
                  placeholder="Enter seed"
                  aria-label="Seed"
                  style={{ minWidth: '220px' }}
                />
                <button className="btn ghost" onClick={() => onRestartWithSeed(seedValue.trim())} disabled={!seedValue.trim()}>
                  Seeded
                </button>
                <button className="btn ghost" onClick={onBack}>
                  Menu
                </button>
              </div>
            ) : (
              <div className="nav-row">
                {onRestartSameMode ? (
                  <button className="btn primary" onClick={onRestartSameMode}>
                    Play again
                  </button>
                ) : null}
                <button className="btn ghost" onClick={onBack}>
                  Menu
                </button>
              </div>
            )}
          </div>
          <div className="xp-footer">
            <ExperienceProgress
              totalExp={totalExp}
              animateFrom={recentGain ? recentGain.from : undefined}
              scaleOnReveal
              gained={recentGain?.gained ?? null}
            />
          </div>
        </>
      ) : null}
    </section>
  );
}
